import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { throttle } from 'lodash-es';

const timing = (1 / 60) * 1000;
const decay = (velocity: number) => -0.008 * ((1 / timing) ^ 4) + velocity;

type DragScrollProps = {
  /** child elements. */
  children?: ReactNode;
  /** Start with scroll at the horizontal end position. */
  initialScrollEnd?: boolean;
  /** Element to scroll into view */
  scrollTo?: null | HTMLElement;
};

type ScrollState = {
  clickStartX?: number;
  scrollStartX?: number;
  isDragging: boolean;
  direction: number;
  momentum: number;
  lastScrollX: number;
  speed: number;
};

const initialScrollState: ScrollState = {
  isDragging: false,
  direction: 0,
  momentum: 0,
  lastScrollX: 0,
  speed: 0,
};

/**
 * A container for horizontal drag scrolling with momentum.
 */
export function DragScroll({
  children,
  'data-testid': dataTestId,
  initialScrollEnd = false,
  scrollTo = null,
}: DragScrollProps & DataTestId) {
  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<ScrollState>(initialScrollState);
  const scrollStateRef = useRef<ScrollState>(scrollState);

  useEffect(() => {
    if (scrollTo && scrollWrapperRef.current) {
      const { left, width } = scrollTo.getBoundingClientRect();
      const documentWidth = document.documentElement.clientWidth;
      const currentPosition = scrollWrapperRef.current.scrollLeft;

      const toZero = currentPosition + left;
      const padding = 24;
      const center = (documentWidth - width - padding * 2) / 2;

      // scroll element to center of screen if possible
      scrollWrapperRef.current.scrollLeft = toZero - padding - center;
    }
  }, [scrollTo]);

  const setNewScrollState = (newScrollState: Partial<ScrollState>): void =>
    setScrollState((currentScrollState) => {
      const state = {
        ...currentScrollState,
        ...newScrollState,
      };
      scrollStateRef.current = state;
      return state;
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLastScrollX = useCallback(
    throttle((screenX) => {
      setNewScrollState({ lastScrollX: screenX });
    }, timing),
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleMomentum = useCallback(
    throttle((nextMomentum) => {
      if (!scrollWrapperRef.current) {
        return;
      }

      setNewScrollState({ momentum: nextMomentum });
      scrollWrapperRef.current.scrollLeft =
        scrollWrapperRef.current.scrollLeft +
        nextMomentum * timing * scrollStateRef.current.direction;
    }, timing),
    [scrollStateRef.current.direction],
  );

  const handleDragStart = (e: MouseEvent) => {
    if (!scrollWrapperRef.current) {
      return;
    }
    setNewScrollState({
      clickStartX: e.screenX,
      scrollStartX: scrollWrapperRef.current.scrollLeft,
      direction: 0,
    });
  };

  const handleDragMove = (e: MouseEvent) => {
    if (!scrollWrapperRef.current) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    const { clickStartX, scrollStartX } = scrollStateRef.current;

    if (clickStartX === undefined || scrollStartX === undefined) {
      return;
    }

    const touchDelta = clickStartX - e.screenX;
    scrollWrapperRef.current.scrollLeft = scrollStartX + touchDelta;
    if (Math.abs(touchDelta) > 1) {
      setNewScrollState({
        isDragging: true,
        direction: touchDelta / Math.abs(touchDelta),
        speed: Math.abs((scrollStateRef.current.lastScrollX - e.screenX) / timing),
      });

      handleLastScrollX(e.screenX);
    }
  };

  const handleDragEnd = () => {
    setNewScrollState({
      clickStartX: undefined,
      scrollStartX: undefined,
      isDragging: false,
    });
  };

  useEffect(() => {
    const { direction, momentum, isDragging, speed } = scrollStateRef.current;
    if (direction !== 0) {
      if (momentum > 0.01 && !isDragging) {
        handleMomentum(decay(momentum));
      } else if (isDragging) {
        setNewScrollState({ momentum: speed });
      } else {
        setNewScrollState({ direction: 0 });
      }
    }
  }, [
    scrollStateRef.current.momentum,
    scrollStateRef.current.isDragging,
    scrollStateRef.current.speed,
    scrollStateRef.current.direction,
    handleMomentum,
  ]);

  useEffect(() => {
    const scrollWrapper = scrollWrapperRef.current;

    if (!scrollWrapper) {
      return;
    }

    if (initialScrollEnd) {
      scrollWrapper.scrollLeft += 999_999;
    }

    scrollWrapper.addEventListener('mousedown', handleDragStart);
    scrollWrapper.addEventListener('mousemove', handleDragMove);
    scrollWrapper.addEventListener('mouseup', handleDragEnd);

    return () => {
      if (!scrollWrapper) {
        return;
      }

      scrollWrapper.removeEventListener('mousedown', handleDragStart as EventListener);
      scrollWrapper.removeEventListener('mousemove', handleDragMove as EventListener);
      scrollWrapper.removeEventListener('mouseup', handleDragEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DragScrollContainer data-testid={dataTestId}>
      <DragScrollWrapper ref={scrollWrapperRef} $isDragging={scrollStateRef.current.isDragging}>
        <ScrollContainer $isDragging={scrollStateRef.current.isDragging}>
          {children}
        </ScrollContainer>
      </DragScrollWrapper>
    </DragScrollContainer>
  );
}

const DragScrollContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const DragScrollWrapper = styled.div<{ $isDragging: boolean }>`
  width: 100%;
  height: 100%;
  overflow: scroll hidden;
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }
  ${({ $isDragging }) =>
    $isDragging &&
    css`
      cursor: grabbing;
    `}
`;

const ScrollContainer = styled.div<{ $isDragging: boolean }>`
  height: 100%;
  display: grid;
  pointer-events: initial;
  min-width: 100%;
  ${({ $isDragging }) =>
    $isDragging &&
    css`
      pointer-events: none;
    `}
`;
